import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import {
  networkRows,
  rewardAmount,
  type NetworkRow,
  type RewardAmount,
  type RewardRulesPayload,
} from './social-rewards';

// The reward program's served rules for the "Earn credits" section — the per-network social reward,
// the referral amounts, and which networks this account has already claimed. Read from
// /api/reward-rules (BalanceService.GetRewardRules), whose amounts are databag-driven: the client
// hard-codes none of them, and renders no number at all when the read fails.

export interface RewardRulesState {
  networks: NetworkRow[];
  socialReward: RewardAmount | null;
  referrerReward: RewardAmount | null;
  premiumConversionReward: RewardAmount | null;
  loading: boolean;
  error: boolean;
  /** Re-read after a successful claim, so the network flips to done from the server's own list. */
  refresh: () => void;
}

export function useRewardRules({ enabled = true }: { enabled?: boolean } = {}): RewardRulesState {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.rewardRules,
    // Gated the same way `useReferrals` is: the share screen stays mounted behind the Settings row,
    // and an ungated read costs an RPC on every render plus a BI load for a modal nobody opened.
    enabled,
    queryFn: async (): Promise<RewardRulesPayload> => {
      const res = await apiFetch(`${API_BASE}/api/reward-rules`, { credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as RewardRulesPayload;
    },
  });

  const data = query.status === 'error' ? undefined : query.data;

  return {
    networks: networkRows(data),
    socialReward: rewardAmount(data?.rules, 'socialReward'),
    referrerReward: rewardAmount(data?.rules, 'referrerReward'),
    premiumConversionReward: rewardAmount(data?.rules, 'premiumConversionReward'),
    // A disabled query sits in 'loading' forever with no fetch in flight, so ask fetchStatus too or a
    // closed modal reports a load that never started.
    loading: query.status === 'loading' && query.fetchStatus !== 'idle',
    error: query.status === 'error',
    refresh: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rewardRules });
      void queryClient.invalidateQueries({ queryKey: queryKeys.creditsBalance });
    },
  };
}
